import { Spacing } from '@persona-id/design-tokens';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import AsyncImage from 'dashboard/components-lib/AsyncImage';
import AsyncSelfie from 'dashboard/components-lib/AsyncSelfie';
import { InquiryModuleAttribute } from 'dashboard/lib/case-layouts/modules';
import { Inquiry } from 'dashboard/models/inquiry';
import { Verification, VerificationGovernmentId } from 'dashboard/models/verification';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { BASE } from 'design-system/variables/spacing';
import { VERIFICATION_PAGE_DISPLAY } from 'lib/constants/verification';
import { ApiModel } from 'lib/interfaces/api';
import { VerificationStatus } from 'lib/verification';

const PhotoStickerContainer = styled('div')`
  position: absolute;
  top: ${Spacing[4]};
  left: ${BASE * 2.5}px;
  z-index: 1;
`;

const PhotoWithStickerContainer = styled('div')`
  position: relative;
  display: grid;
  grid-column-gap: ${Spacing[4]};
  grid-template-rows: auto auto;
`;

interface InquiryPhotosInterface {
  inquiry: Inquiry;
  verifications: Verification[];
  isRedacted: boolean;
}

const InquiryPhotos = ({ inquiry, verifications = [], isRedacted }: InquiryPhotosInterface) => {
  const { 'selfie-photo-url': selfiePhotoUrl } = inquiry.attributes;

  const shouldShowSelfiePhoto: boolean = verifications.some(
    (v) => v.type === ApiModel.VerificationSelfie,
  );

  const governmentIdVerifications: VerificationGovernmentId[] = verifications.filter(
    (v): v is VerificationGovernmentId => {
      return v.type === ApiModel.VerificationGovernmentId;
    },
  );

  const passedGovIdVerifications = governmentIdVerifications.filter(
    (v) => v.attributes.status === VerificationStatus.Passed,
  );

  const governmentIdVerification = passedGovIdVerifications?.[0] ?? governmentIdVerifications?.[0];
  const govIdFrontPhoto = governmentIdVerification?.attributes['front-photo-url'];
  const govIdBackPhoto = governmentIdVerification?.attributes['back-photo-url'];
  const govIdDisplayPhotos = governmentIdVerification?.attributes['photo-urls'];

  const hasPhoto = govIdFrontPhoto || govIdBackPhoto || govIdDisplayPhotos || shouldShowSelfiePhoto;

  // If there are 1 or 2 photos, use 2 columns. Otherwise, use 3 columns.
  const photoLength =
    [govIdFrontPhoto, govIdBackPhoto, shouldShowSelfiePhoto].filter(Boolean).length +
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    govIdDisplayPhotos?.length;
  const photoCols = photoLength > 2 ? 3 : 2;

  const photos = (
    <>
      {govIdFrontPhoto && (
        <>
          <Link
            to={`/dashboard/document-viewer/${governmentIdVerification?.relationships?.document?.data?.id}`}
            state={{ internalRedirect: true }}
          >
            <PhotoWithSticker
              imageUrl={govIdFrontPhoto}
              isRedacted={isRedacted}
              modalLabel='Front'
              stickerLabel='Front'
            />
          </Link>
        </>
      )}
      {govIdBackPhoto && (
        <>
          <Link
            to={`/dashboard/document-viewer/${governmentIdVerification?.relationships?.document?.data?.id}`}
            state={{ internalRedirect: true }}
          >
            <PhotoWithSticker
              imageUrl={govIdBackPhoto}
              isRedacted={isRedacted}
              modalLabel='Back'
              stickerLabel='Back'
            />
          </Link>
        </>
      )}
      {govIdDisplayPhotos &&
        govIdDisplayPhotos.map((govIdDisplayPhoto) => (
          <>
            <Link
              to={`/dashboard/document-viewer/${governmentIdVerification?.relationships?.document?.data?.id}`}
              state={{ internalRedirect: true }}
            >
              <PhotoWithSticker
                key={govIdDisplayPhoto['url']}
                imageUrl={govIdDisplayPhoto['url']}
                isRedacted={isRedacted}
                modalLabel={VERIFICATION_PAGE_DISPLAY[govIdDisplayPhoto['page']]}
                stickerLabel={VERIFICATION_PAGE_DISPLAY[govIdDisplayPhoto['page']]}
              />
            </Link>
          </>
        ))}
      {selfiePhotoUrl && shouldShowSelfiePhoto && (
        <PhotoWithSticker
          imageUrl={selfiePhotoUrl}
          isRedacted={isRedacted}
          modalLabel='Selfie'
          stickerLabel='Selfie'
          isSelfie={true}
          sourceToken={inquiry.id}
        />
      )}
    </>
  );

  return (
    <>
      <Spacer height={4} />
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <Attribute label='Photos' placeholder={hasPhoto ? null : 'No photos collected'} />
      <AttributeGrid
        data-test={`inquiry-module__${InquiryModuleAttribute.Photos}`}
        colCount={photoCols}
      >
        {hasPhoto && photos}
      </AttributeGrid>
      <Spacer height={4} />
    </>
  );
};

interface PhotoWithStickerProps {
  imageUrl: string;
  isRedacted: boolean;
  modalLabel: string;
  stickerLabel: string;
  isSelfie?: boolean;
  sourceToken?: string;
}

const PhotoWithSticker = ({
  imageUrl,
  isRedacted,
  modalLabel,
  stickerLabel,
  isSelfie = false,
  sourceToken,
}: PhotoWithStickerProps) => {
  return (
    <PhotoWithStickerContainer>
      <PhotoStickerContainer>
        <Sticker variant='primary'>{stickerLabel}</Sticker>
      </PhotoStickerContainer>
      {isSelfie ? (
        <AsyncSelfie
          value={isRedacted ? [] : [{ url: imageUrl }]}
          width='100%'
          height='270px'
          modalLabel={modalLabel}
          sourceToken={sourceToken}
        />
      ) : (
        <AsyncImage
          value={isRedacted ? [] : [{ url: imageUrl }]}
          width='100%'
          height='270px'
          modalLabel={modalLabel}
        />
      )}
    </PhotoWithStickerContainer>
  );
};

export default InquiryPhotos;
